










































































































import VueElementLoading from "vue-element-loading"
import VueDraggable from 'vuedraggable'
import { Vue, Component, Prop } from 'vue-property-decorator'
import moment from "moment"
import { cloneDeep } from "lodash"
import { ReportSvodRasRow } from "../table-data/ReportSvodRasRow"
import { Classificator } from "../../../common/types/Classificator"
import { Indicator } from "../../../common/types/Indicator"
import { formatDigitsGrouping } from "../../../common/utils/formattingUtils"
import { Draggable } from "../../../common/types/Draggable"
import { Segmentation } from "../../../common/types/roots/fields"

interface TableField {
    key: string
    label: string
    child_list: TableField[]
    colspan: number
    rowspan: number
}

/**
 * Соответствие показателя SVOD-RAS родительской группе года.
 * `false` - показатель без родителя (расчетная колонка отклонения).
 */
const parentGroupByIndicator: Record<string, string | false> = {
    plan_year_y2: 'year_y2',
    fact_year_end_y2: 'year_y2',
    plan_date_y2: 'year_y2',
    fact_date_y2: 'year_y2',
    plan_year_y1: 'year_y1',
    fact_year_end_y1: 'year_y1',
    plan_date_y1: 'year_y1',
    fact_date_y1: 'year_y1',
    dev_fact_y1_y2: false,
    plan_year: 'year_cur',
    plan_date: 'year_cur',
    fact_date: 'year_cur',
    dev_fact_y_y1: false,
}

/** Показатели, которые не выводятся при отключенном сравнении с прошлым годом (RQ.eAk.SvodRas.29) */
const hiddenWhenNoComparePrevYear = [
    'plan_year_y2', 'fact_year_end_y2', 'plan_date_y2', 'fact_date_y2',
    'plan_year_y1', 'fact_year_end_y1', 'plan_date_y1', 'fact_date_y1',
    'dev_fact_y1_y2',
]

@Component({
    components: {
        'draggable': VueDraggable,
        'loading': VueElementLoading,
    }
})
export default class ReportSvodRas extends Vue {

    @Prop({
        type: Array,
        required: true,
        default: [],
    })
    public readonly tableData!: ReportSvodRasRow[]

    @Prop({
        type: Array,
        required: true,
        default: [],
    })
    private readonly propIndicators!: Indicator[]

    @Prop({
        type: Array,
        required: true,
        default: [],
    })
    private readonly expenses!: Classificator[]

    /** Разрезность (drag-порядок REGION/KBK/GU/...) - определяет порядок колонок в дереве (RQ.eAk.SvodRas) */
    @Prop({
        type: Array,
        required: true,
        default: [],
    })
    private readonly segmentations!: Draggable<Segmentation>[]

    @Prop({
        type: Date,
        required: true,
        default: new Date(),
    })
    private readonly date!: Date

    @Prop({
        type: Boolean,
        required: true,
        default: false,
    })
    private readonly isTree!: boolean

    @Prop({
        type: Boolean,
        required: true,
        default: true,
    })
    private readonly comparePrevYear!: boolean

    public tableFields: TableField[] = []
    public classifications: string[] = []
    public indicators: Indicator[] = []

    public currentPage = 1
    public get visibleRows() {
        return this.tableData.filter(it => it.visible).length
    }
    public get currentItems(): ReportSvodRasRow[] {
        if (this.isTree) {
            return this.tableData.filter(it => it.visible)
        } else {
            const indexedPage = this.currentPage - 1
            const startIndex = indexedPage * 10
            const endIndex = startIndex + 10
            return this.tableData.filter(it => it.visible).slice(startIndex, endIndex)
        }
    }

    private created() {
        this.indicators = cloneDeep(this.propIndicators)
        this.constructTableFields()
        this.handlePageChange(1)
    }

    /** Год отчета (Y) и предыдущие два года (Y-1, Y-2), вычисленные от даты фильтра */
    private get years(): { y: number, y1: number, y2: number } {
        const y = moment(this.date).year()
        return { y, y1: y - 1, y2: y - 2 }
    }

    /** Отчетная дата и та же дата в предыдущие два года */
    private get dates(): { date: string, date_y1: string, date_y2: string } {
        const m = moment(this.date)
        return {
            date: m.format('DD.MM.YYYY'),
            date_y1: m.clone().subtract(1, 'years').format('DD.MM.YYYY'),
            date_y2: m.clone().subtract(2, 'years').format('DD.MM.YYYY'),
        }
    }

    private constructTableFields() {
        // 1. Классификация (лестница кодов бюджетной классификации + Регион/ГУ из «Разрезности»)
        let activeFields: string[] = ['action']
        if (this.isTree) {
            this.classifications = this.buildColumnOrder()
            activeFields = activeFields.concat(this.classifications)
        } else {
            this.classifications = []
        }
        activeFields.push('name')

        // 2. Показатели (с учетом «Сравнение с прошлым годом» - RQ.eAk.SvodRas.29)
        const activeIndicatorKeys = this.indicators
            .filter(it => it.active)
            .filter(it => this.comparePrevYear || !hiddenWhenNoComparePrevYear.includes(it.key))
            .map(it => it.key)

        this.tableFields = this.constructParentFields(activeFields.concat(activeIndicatorKeys))
    }

    /**
     * Порядок колонок дерева: drag-порядок «Разрезности» (REGION -> 'region', GU -> 'gu',
     * KBK -> активные классификаторы «Расходы» ФГ/ФПГ/АБП/ГУ/БП/БПП/СПФ), плюс доменное
     * правило переноса 'gu' сразу после 'abp'.
     *
     * Не переиспользует бэкендовый общий setColList (cash_execution_report_2_19_4_20_table.py,
     * общий с CASH-EXEC) - там якорение 'gu' после 'abp' срабатывает только если ГУ не первый
     * и не последний в cutness, что дает непредсказуемый порядок на краях. Для SVOD-RAS
     * якорение безусловное (подтверждено analyst-budget: ГУ в ЕБК - звено классификатора
     * расходов строго под своим АБП) - см. build_col_list в
     * back/.../cash_execution_report_svod_ras.py, эта функция обязана повторять её 1-в-1,
     * т.к. бэкенд уже строит иерархию строк (cash_execution_report_tree) в этом порядке колонок.
     */
    private buildColumnOrder(): string[] {
        const cutness: string[] = (this.segmentations ?? []).filter(it => it.active).map(it => it.value)
        const existingCols: string[] = cloneDeep(this.expenses).filter(it => it.active).map(it => it.key)

        let orderList: string[] = []
        for (const c of cutness) {
            if (c === 'REGION') {
                orderList.push('region')
            }
            if (c === 'GU') {
                orderList.push('gu')
            }
            if (c === 'KBK') {
                orderList = orderList.concat(existingCols)
            }
        }

        if (this.isTree && orderList.includes('gu') && orderList.includes('abp')) {
            const oidx = orderList.indexOf('gu')
            const [gu] = orderList.splice(oidx, 1)
            orderList.splice(orderList.indexOf('abp') + 1, 0, gu)
        }

        // Дедуп ГУ: разрезность GU и активный чекбокс «ГУ:» в «Расходах» могут дать дубль
        // колонки 'gu' - оставляем первое вхождение, остальные убираем.
        let seenGu = false
        const deduped: string[] = []
        for (const c of orderList) {
            if (c === 'gu') {
                if (seenGu) {
                    continue
                }
                seenGu = true
            }
            deduped.push(c)
        }
        return deduped
    }

    private constructParentFields(activeFields: string[]): TableField[] {
        const groups: Record<string, TableField> = {}
        const order: string[] = []
        for (const key of activeFields) {
            const child: TableField = {
                key: key,
                label: key,
                child_list: [],
                colspan: 1,
                rowspan: 1,
            }
            const groupKey = parentGroupByIndicator[key]
            if (groupKey) {
                let parentNode = groups[groupKey]
                if (!parentNode) {
                    parentNode = {
                        key: groupKey,
                        label: groupKey,
                        child_list: [],
                        colspan: 0,
                        rowspan: 1,
                    }
                    groups[groupKey] = parentNode
                    order.push(groupKey)
                }
                parentNode.child_list.push(child)
                parentNode.colspan++
            } else {
                child.rowspan = 2
                groups[key] = child
                order.push(key)
            }
        }
        return order.map(key => groups[key])
    }

    public synchronizeIndicatorsOrder() {
        // Пересобираем порядок показателей из текущего порядка блоков в шапке
        const newOrderedKeys: string[] = []
        for (const field of this.tableFields) {
            if (field.child_list.length > 0) {
                for (const child of field.child_list) {
                    newOrderedKeys.push(child.key)
                }
            } else if (this.indicators.some(it => it.key === field.key)) {
                newOrderedKeys.push(field.key)
            }
        }
        // Показатели, скрытые из-за отключенного сравнения с прошлым годом, сохраняем в исходном относительном порядке в конце
        this.indicators.forEach(it => {
            if (!newOrderedKeys.includes(it.key)) {
                newOrderedKeys.push(it.key)
            }
        })

        const newIndicators: Indicator[] = []
        newOrderedKeys.forEach(key => {
            const found = this.indicators.find(it => it.key === key)
            if (found) {
                newIndicators.push(found)
            }
        })
        this.indicators = newIndicators
        this.$emit('update-indicators', cloneDeep(newIndicators))
        this.constructTableFields()
    }

    public synchronizeVisiblityInTable() {
        this.$emit('update-indicators', cloneDeep(this.indicators))
        this.constructTableFields()
    }

    public handlePageChange(page: number) {
        this.currentPage = page
    }

    public tr(key: any): string {
        if (typeof key !== 'string') {
            return ''
        }

        const tableTranslationKey = 'modules.budget_execution_monitoring.reports_constructor.svod_ras_report.table'
        let text = this.$t(`${tableTranslationKey}.${key}`).toString()

        if (!text || text === `${tableTranslationKey}.${key}`) {
            const translationKey = 'modules.budget_execution_monitoring.reports_constructor'
            text = this.$t(`${translationKey}.${key}`).toString()

            if (!text || text === `${translationKey}.${key}`) {
                text = key
            }
        }

        // Порядок замены важен: более длинные плейсхолдеры заменяются первыми
        const { y, y1, y2 } = this.years
        const { date, date_y1, date_y2 } = this.dates
        text = text.replaceAll('$year_y2', String(y2))
        text = text.replaceAll('$year_y1', String(y1))
        text = text.replaceAll('$year', String(y))
        text = text.replaceAll('$date_y2', date_y2)
        text = text.replaceAll('$date_y1', date_y1)
        text = text.replaceAll('$date', date)

        return text
    }

    public format(value: any): any {
        if (value == null) {
            return ''
        }
        if (Number(value).isNaN) {
            return value
        }
        // RQ.40: дробная часть — через запятую (русская локаль), а не точку. formatDigitsGrouping
        // только группирует разряды тысяч пробелом и не трогает разделитель дробной части —
        // общую утилиту не меняем (ей пользуются и другие отчёты, например CASH-EXEC), правим
        // только здесь. .replace() без /g заменяет первое (и единственное для суммы) вхождение.
        return formatDigitsGrouping(value).replace('.', ',')
    }

    public tableCollapse = true
    public collapse(idx: number, collapse: boolean, collapseAll = false) {
        const el: ReportSvodRasRow = this.tableData[idx]
        if (el.hasChild) {
            for (const ch of this.tableData.slice(idx + 1)) {
                if (ch.parent_idx === idx) {
                    ch.visible = !collapse
                    if (collapse || collapseAll) {
                        this.collapse(ch['idx'], collapse, collapseAll)
                        ch.collapse = collapse
                    }
                }
            }
        }
        el.collapse = collapse
    }
    public collapseAll(collapse: boolean) {
        for (const item of this.tableData) {
            if (item.parent_idx === null && item.hasChild) {
                item.collapse = collapse
                this.collapse(item.idx, collapse, true)
            }
        }
        this.tableCollapse = collapse
    }
}
